import { db } from '@/config/firebase';

export default {
    namespaced: true,
    state: {
        // 'online' | 'connecting' | 'offline' | 'server-unreachable'
        status: 'online',
        // Whether the browser has network access
        browserOnline: true,
        // Whether Firebase can reach the server
        firebaseConnected: true,
        // Has Firebase connected at least once this session?
        // Prevents false positive on initial load (RTDB starts as disconnected)
        hasConnectedOnce: false,
        // Timer for escalating "connecting" → "server-unreachable"
        escalationTimer: null,
        // Briefly show "Connected" after reconnecting
        showReconnected: false,
        // Listener cleanup
        _unsubscribe: null,
    },
    mutations: {
        setBrowserOnline(state, value) {
            state.browserOnline = value;
        },
        setFirebaseConnected(state, value) {
            state.firebaseConnected = value;
        },
        setHasConnectedOnce(state) {
            state.hasConnectedOnce = true;
        },
        setStatus(state, value) {
            state.status = value;
        },
        setEscalationTimer(state, timer) {
            state.escalationTimer = timer;
        },
        setShowReconnected(state, value) {
            state.showReconnected = value;
        },
        setUnsubscribe(state, fn) {
            state._unsubscribe = fn;
        },
    },
    actions: {
        startListening({ commit, dispatch }) {
            // Browser online/offline events
            const onOnline = () => dispatch('onBrowserOnline');
            const onOffline = () => dispatch('onBrowserOffline');
            window.addEventListener('online', onOnline);
            window.addEventListener('offline', onOffline);
            commit('setBrowserOnline', navigator.onLine);

            // Firebase Realtime Database connection state
            const connectedRef = db.ref('.info/connected');
            connectedRef.on('value', (snap) => {
                if (snap.val() === true) {
                    dispatch('onFirebaseConnected');
                } else {
                    dispatch('onFirebaseDisconnected');
                }
            });

            commit('setUnsubscribe', () => {
                connectedRef.off();
                window.removeEventListener('online', onOnline);
                window.removeEventListener('offline', onOffline);
            });
        },

        onBrowserOffline({ commit, dispatch }) {
            commit('setBrowserOnline', false);
            dispatch('updateStatus');
        },

        onBrowserOnline({ commit, dispatch }) {
            commit('setBrowserOnline', true);
            dispatch('updateStatus');
        },

        onFirebaseConnected({ commit, state, dispatch }) {
            commit('setFirebaseConnected', true);
            commit('setHasConnectedOnce');

            // Clear escalation timer
            if (state.escalationTimer) {
                clearTimeout(state.escalationTimer);
                commit('setEscalationTimer', null);
            }

            dispatch('updateStatus');
        },

        onFirebaseDisconnected({ commit, dispatch }) {
            commit('setFirebaseConnected', false);
            dispatch('updateStatus');
        },

        updateStatus({ commit, state }) {
            const wasBad = state.status !== 'online';

            if (!state.browserOnline) {
                // No network at all — always show this immediately
                commit('setStatus', 'offline');

                if (state.escalationTimer) {
                    clearTimeout(state.escalationTimer);
                    commit('setEscalationTimer', null);
                }
            } else if (!state.firebaseConnected && state.hasConnectedOnce) {
                // Network up but Firebase disconnected AFTER a previous connection
                // (Skip if we haven't connected once yet — avoids false positive on load)
                if (state.status !== 'server-unreachable') {
                    commit('setStatus', 'connecting');

                    // After 5 seconds, escalate to "server-unreachable"
                    if (!state.escalationTimer) {
                        const timer = setTimeout(() => {
                            if (!state.firebaseConnected && state.browserOnline) {
                                commit('setStatus', 'server-unreachable');
                            }
                            commit('setEscalationTimer', null);
                        }, 5000);
                        commit('setEscalationTimer', timer);
                    }
                }
            } else {
                // All good (or initial load before first connection)
                commit('setStatus', 'online');

                if (state.escalationTimer) {
                    clearTimeout(state.escalationTimer);
                    commit('setEscalationTimer', null);
                }

                // Show "Connected" briefly after a reconnect
                if (wasBad) {
                    commit('setShowReconnected', true);
                    setTimeout(() => {
                        commit('setShowReconnected', false);
                    }, 2000);
                }
            }
        },

        stopListening({ state, commit }) {
            if (state._unsubscribe) {
                state._unsubscribe();
                commit('setUnsubscribe', null);
            }
            if (state.escalationTimer) {
                clearTimeout(state.escalationTimer);
                commit('setEscalationTimer', null);
            }
        },
    },
    getters: {
        isOnline(state) {
            return state.status === 'online';
        },
        showBanner(state) {
            return state.status !== 'online' || state.showReconnected;
        },
        bannerMessage(state) {
            switch (state.status) {
                case 'offline':
                    return 'No internet connection';
                case 'connecting':
                    return 'Connecting...';
                case 'server-unreachable':
                    return 'Unable to connect — check your connection, VPN, or firewall';
                default:
                    return 'Connected';
            }
        },
        bannerType(state) {
            if (state.status === 'online' && state.showReconnected) return 'success';
            if (state.status === 'offline') return 'error';
            if (state.status === 'server-unreachable') return 'warning';
            return 'info'; // connecting
        },
    },
};
